<script setup lang="ts">
// ============================================================================
// Imports
// ============================================================================
import { onMounted, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { formatTimestamp, formatDate } from '@/utils/helpers/dateTime'
import { useVerification } from '@/services/api/useVerification'
import type { VerificationResponse } from '@/interfaces/verification'
import HeroSection from '@/components/common/HeroSection.vue'
import BrandingFooter from '@/components/common/BrandingFooter.vue'

// ============================================================================
// Reactive State
// ============================================================================
const route = useRoute()
const router = useRouter()

// Stores the verification data fetched from the API
const verification = ref<VerificationResponse | null>(null)

// Tracks loading state while fetching verification data
const isLoading = ref(false)

// ============================================================================
// API Service
// ============================================================================
const { fetchVerification } = useVerification()

// ============================================================================
// Functions
// ============================================================================

/**
 * Fetches verification details for a given hash
 * Cache is automatically handled by fetchVerification
 * @param hash - The verification hash identifier
 */
const fetchVerificationDetail = async (hash: string) => {
  await fetchVerification(hash, verification, isLoading)
}

// ============================================================================
// Computed Properties
// ============================================================================

/**
 * Combines template fields with actual verification data
 * Maintains the exact order from page_template_content.verificationData
 * Maps template field definitions to their corresponding values from verification_data
 */
const verificationFields = computed(() => {
  if (!verification.value?.page_template_content?.verificationData) {
    return []
  }
  
  const templateFields = verification.value.page_template_content.verificationData
  const dataFields = verification.value.verification_data || []
  
  // Using map() preserves the original order from templateFields
  return templateFields.map(templateField => {
    const dataField = dataFields.find(data => data.fieldId === templateField.fieldId)

    // If "Created On" is coming from verification_data as an ISO string, format it for display
    const rawValue = dataField?.fieldValue
    const isCreatedOn = templateField.fieldName?.toLowerCase() === 'created on'
    const shouldFormatCreatedOn =
      isCreatedOn && typeof rawValue === 'string' && rawValue.trim().length > 0 && !Number.isNaN(Date.parse(rawValue))

    return {
      ...templateField,
      fieldValue: shouldFormatCreatedOn
        ? formatTimestamp(rawValue as string)
        : rawValue || getDefaultValue(templateField.fieldName)
    }
  })
})

/**
 * Determines whether the document has passed its expiry date
 * Returns true if expiry date exists and is in the past
 */
const isExpired = computed(() => {
  const expiry = verification.value?.expiry
  if (!expiry) return false
  return new Date(expiry).getTime() <= Date.now()
})

/**
 * Fraud reporting is controlled by backend flag.
 * Only show link when explicitly enabled.
 */
const isFraudReportEnabled = computed(() => {
  const v = verification.value
  if (!v) return false
  // Prefer new field name, but support legacy fallback
  return Boolean(v.fraud_enable ?? v.fraud_report_enabled)
})

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets default values for standard fields that may not be in verification_data
 * @param fieldName - The name of the field to get a default value for
 * @returns The default value for the field, or empty string if not found
 */
const getDefaultValue = (fieldName: string) => {
  if (!verification.value) return ''
  
  switch (fieldName.toLowerCase()) {
    case 'document name':
      return verification.value.document_name
    case 'created by':
      return verification.value.created_name
    case 'created on':
      return formatTimestamp(verification.value.created)
    case 'verification id':
      return verification.value.hash
    default:
      return ''
  }
}


// ============================================================================
// Lifecycle Hooks
// ============================================================================

/**
 * Fetches verification details when component is mounted
 * Uses the hash parameter from the route
 */
onMounted(() => route.params.hash && fetchVerificationDetail(route.params.hash as string))
</script>

<template>
  <div class="min-h-screen bg-gray-50">
    <!-- ===================================================================== -->
    <!-- Hero Section -->
    <!-- ===================================================================== -->
    <HeroSection :verification="verification" />

    <!-- ===================================================================== -->
    <!-- Main Content Area -->
    <!-- ===================================================================== -->
    <div class="container mx-auto px-4 -mt-4 pb-8">
      <!-- Verification Details Card -->
      <div class="bg-white rounded-lg shadow-lg overflow-hidden mb-4">
        <!-- ================================================================= -->
        <!-- Loading State -->
        <!-- ================================================================= -->
        <!-- Shows spinner while fetching verification data -->
        <div v-if="isLoading" class="text-center py-8">
          <div class="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full mx-auto mb-2"></div>
          <p class="text-gray-500 text-sm">Loading document details...</p>
        </div>

        <!-- ================================================================= -->
        <!-- Document Not Found State -->
        <!-- ================================================================= -->
        <!-- Shown when verification data is null (document doesn't exist) -->
        <div v-else-if="!verification" class="text-center py-8">
          <div class="text-gray-400 mb-2">
            <svg class="w-10 h-10 mx-auto" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            </svg>
          </div>
          <h3 class="text-base font-semibold text-gray-700 mb-1">No Document Found</h3>
          <p class="text-gray-500 text-xs">We can't find this document. Please check your link and try again.</p>
        </div>

        <!-- ================================================================= -->
        <!-- Document Unavailable State -->
        <!-- ================================================================= -->
        <!-- Shown when verification exists but availability is false -->
        <div v-else-if="!verification.availability" class="text-center py-8">
          <div class="text-red-400 mb-2">
            <svg class="w-10 h-10 mx-auto" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M16.18,7.76L15.12,6.7L12,9.82L8.88,6.7L7.82,7.76L10.94,10.88L7.82,14L8.88,15.06L12,11.94L15.12,15.06L16.18,14L13.06,10.88L16.18,7.76Z" />
            </svg>
          </div>
          <h3 class="text-base font-semibold text-gray-700 mb-1">Document Expired</h3>
          <p class="text-gray-600 text-xs max-w-md mx-auto">
            This document verification is no longer active. If you're the owner, please reach out to us at
            <a href="mailto:support@qrmark.com" class="text-secondary-600 hover:underline">
              support@qrmark.com
            </a>
          </p>
        </div>

        <!-- ================================================================= -->
        <!-- Verification Details Content -->
        <!-- ================================================================= -->
        <!-- Displays verification fields and document information -->
        <div v-else class="p-4">
          <div class="grid lg:grid-cols-[1fr_auto] gap-4 items-start">
            <div>
              <!-- ============================================================= -->
              <!-- Verification Fields Grid -->
              <!-- ============================================================= -->
              <!-- Template fields when available; otherwise fallback defaults. -->
              <!-- Expiry is always appended when verification.expiry is set. -->
              <div class="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                <template v-if="verificationFields.length > 0">
                  <div
                    v-for="field in verificationFields"
                    :key="field.fieldId"
                    class="bg-gray-50 rounded-lg p-3 border border-gray-100"
                  >
                    <dt class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {{ field.fieldName }}
                    </dt>
                    <dd class="text-sm font-semibold text-gray-900 wrap-break-word">
                      <div v-if="isLoading" class="loader"></div>
                      <span v-else>{{ field.fieldValue || 'Not provided' }}</span>
                    </dd>
                  </div>
                </template>

                <template v-else>
                  <div class="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <dt class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Document Name</dt>
                    <dd class="text-sm font-semibold text-gray-900 wrap-break-word">
                      <div v-if="isLoading" class="loader"></div>
                      <span v-else>{{ verification.document_name }}</span>
                    </dd>
                  </div>

                  <div class="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <dt class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Verification ID</dt>
                    <dd class="text-sm font-semibold text-gray-900">
                      <div v-if="isLoading" class="loader"></div>
                      <span v-else>{{ verification.hash }}</span>
                    </dd>
                  </div>

                  <div class="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <dt class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Created On</dt>
                    <dd class="text-sm font-semibold text-gray-900">
                      <div v-if="isLoading" class="loader"></div>
                      <span v-else>{{ formatTimestamp(verification.created) }}</span>
                    </dd>
                  </div>

                  <div class="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <dt class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Created By</dt>
                    <dd class="text-sm font-semibold text-gray-900">
                      <div v-if="isLoading" class="loader"></div>
                      <span v-else>{{ verification.created_name }}</span>
                    </dd>
                  </div>
                </template>

                <!-- Expiry Date Field (shown for both template and fallback layouts) -->
                <div
                  v-if="verification.expiry"
                  :class="[
                    'rounded-lg p-3 border transition-colors',
                    isExpired ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-100'
                  ]"
                >
                  <dt
                    :class="[
                      'text-xs font-medium uppercase tracking-wide mb-1',
                      isExpired ? 'text-amber-700' : 'text-gray-500'
                    ]"
                  >
                    Expiry Date
                  </dt>
                  <dd :class="['text-sm font-semibold wrap-break-word', isExpired ? 'text-amber-800' : 'text-gray-900']">
                    <div v-if="isLoading" class="loader"></div>
                    <div v-else class="flex items-start justify-between gap-3">
                      <div>
                        <div>{{ formatDate(verification.expiry) }}</div>
                        <p v-if="isExpired" class="text-xs font-medium text-amber-700 mt-1">
                          {{ verification.expiry_message || 'This document has expired.' }}
                        </p>
                      </div>
                      <span
                        v-if="isExpired"
                        class="inline-flex items-center bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-3 py-1 text-[11px] font-bold tracking-wide uppercase shrink-0"
                      >
                        Expired
                      </span>
                    </div>
                  </dd>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ================================================================= -->
      <!-- Demo Warning Banner -->
      <!-- ================================================================= -->
      <!-- Displays warning when document is from a demo domain -->
      <div v-if="verification?.has_demo_domain" 
           class="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
        <div class="flex items-start">
          <div class="shrink-0">
            <svg class="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12,2L13.09,8.26L22,9L13.09,9.74L12,16L10.91,9.74L2,9L10.91,8.26L12,2M12,4.74L11.45,7.76L8.43,8.26L11.45,8.76L12,11.74L12.55,8.76L15.57,8.26L12.55,7.76L12,4.74Z" />
            </svg>
          </div>
          <div class="ml-2">
            <h3 class="text-xs font-medium text-amber-800 mb-1">Demo Mode</h3>
            <p class="text-xs text-amber-700">
              This is a demo verification page. The details and document shown here might not be the original and should not be treated as authentic.
            </p>
          </div>
        </div>
      </div>

      <!-- ================================================================= -->
      <!-- Action Buttons -->
      <!-- ================================================================= -->
      <!-- Button to view the original document (opens in new tab) -->
      <div class="text-center mb-4">
        <a
          v-if="verification && verification.document_url"
          class="inline-flex items-center bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-medium transition-colors shadow-xs text-xs"
          :href="verification.document_url"
          target="_blank"
        >
          <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" />
          </svg>
         <span>View <span v-if="!verification?.has_demo_domain"> Original</span> <span v-else> Demo</span> Document</span>
        </a>
      </div>

      <!-- ================================================================= -->
      <!-- Report Fraud Link -->
      <!-- ================================================================= -->
      <!-- Link to report suspected fraud -->
      <div v-if="verification && isFraudReportEnabled" class="text-center mb-4">
        <p class="text-xs text-gray-500">
          Suspect fraud or tampering?
          <button
            @click="router.push(`/${route.params.hash}/report-fraud`)"
            class="text-secondary-600 hover:text-secondary-700 hover:underline font-medium transition-colors"
          >
            Send Report.
          </button>
        </p>
      </div>

      <!-- ================================================================= -->
      <!-- QR Mark Branding Footer -->
      <!-- ================================================================= -->
      <BrandingFooter :verification="verification" />
    </div>
  </div>
</template>

<!-- ===================================================================== -->
<!-- Component Styles -->
<!-- ===================================================================== -->
<!--suppress CssUnusedSymbol -->
<style scoped lang="postcss">
@reference "tailwindcss";

/* Loading skeleton animation */
.loader {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  @apply bg-gray-300 rounded h-4 w-20;
}
</style>
